import { z } from "zod";
export const platformRoles = [
    "platform_owner",
    "security_admin",
    "tenant_operator",
    "support_operator",
    /** 商业运营：订单、账单、线下收款、退款申请、对账。不能改变功能权限或平台凭据。 */
    "billing_operator",
    "auditor",
];
export const platformRoleSchema = z.enum(platformRoles);
export const platformActions = [
    "dashboard.read",
    "tenant.read",
    "tenant.create",
    "tenant.lifecycle",
    "tenant.entitlements",
    "tenant.quotas",
    "tenant.readiness",
    "tenant.applications.read",
    "tenant.applications.manage",
    // 租户档案元数据（负责人/联系人）维护；不涉及生命周期与授权
    "tenant.update",
    "credential.read_metadata",
    "credential.write",
    "credential.rotate",
    "credential.revoke",
    "credential.rollback",
    "service_config.read",
    "service_config.manage",
    "service_config.verify",
    "service_config.test",
    "admin.read",
    "admin.manage",
    "support.grant",
    "support.approve",
    "support.activate",
    "support.revoke",
    "audit.read",
    "health.read",
    "runtime.manage",
    "runtime.print_agent_policy.read",
    "runtime.print_agent_policy.write",
    "quota.manage",
    "plans.manage",
    "usage.read",
    "connections.manage",
    "delivery.manage",
    "enterprise.manage",
    "approvals.manage",
    "operations.manage",
    "feature_flags.manage",
    "assistant.chat",
    // ---- 商业底座（P0 新增）----
    // 能力目录与计量目录
    "catalog.read",
    "catalog.sync",
    "catalog.category_attributes.read",
    "catalog.category_attributes.manage",
    "runtime.category_attributes.read",
    // 套餐：读、编辑版本、发布
    "plans.read",
    "plans.version.manage",
    "plans.publish",
    // 订阅
    "subscriptions.read",
    "subscriptions.purchase",
    "subscriptions.change",
    "subscriptions.projection.retry",
    // 订购单
    "orders.read",
    "orders.create",
    "orders.confirm_payment",
    "orders.cancel",
    // 账单与开票资料
    "invoices.read",
    "invoices.issue",
    "invoices.adjust",
    "billing_profiles.manage",
    // 支付与退款
    "payments.read",
    "payments.confirm_offline",
    "refunds.read",
    "refunds.request",
    "refunds.approve",
    "refunds.execute",
    // 用量与人工例外
    "usage.manage",
    "usage.adjust",
    "overrides.manage",
    // 对账
    "reconciliation.read",
    "reconciliation.run",
    "reconciliation.resolve",
];
export const platformActionSchema = z.enum(platformActions);
export const roleActions = {
    platform_owner: platformActions,
    security_admin: [
        "dashboard.read", "tenant.read", "credential.read_metadata", "credential.write",
        "credential.rotate", "credential.revoke", "credential.rollback",
        "service_config.read", "service_config.manage", "service_config.verify", "service_config.test",
        "admin.read", "admin.manage",
        "support.approve", "support.revoke", "audit.read", "health.read",
        "runtime.manage", "connections.manage", "delivery.manage", "enterprise.manage",
        "runtime.print_agent_policy.read", "runtime.print_agent_policy.write",
        "approvals.manage", "operations.manage", "feature_flags.manage", "quota.manage",
        "assistant.chat",
        // 安全角色可以看见商业配置但不能改价、不能确认收款：职责分离要求确认收入与改价分离。
        "catalog.read", "catalog.category_attributes.read", "plans.read", "subscriptions.read",
        "orders.read", "invoices.read", "payments.read", "refunds.read",
        "reconciliation.read", "usage.read",
    ],
    tenant_operator: [
        "dashboard.read", "tenant.read", "tenant.create", "tenant.lifecycle",
        "tenant.entitlements", "tenant.quotas", "tenant.readiness", "tenant.applications.read", "tenant.applications.manage", "tenant.update", "audit.read", "health.read",
        "runtime.manage", "plans.manage", "usage.read", "connections.manage", "delivery.manage", "enterprise.manage", "operations.manage", "feature_flags.manage",
        "runtime.print_agent_policy.read", "quota.manage", "assistant.chat", "service_config.read",
        // 租户运营负责套餐与订阅的日常运营，但不碰收款与退款。
        "catalog.read", "catalog.category_attributes.read", "plans.read", "plans.version.manage",
        "subscriptions.read", "subscriptions.purchase", "subscriptions.change", "subscriptions.projection.retry",
        "orders.read", "orders.create", "orders.cancel",
        "invoices.read", "billing_profiles.manage",
        "usage.manage", "overrides.manage",
        "reconciliation.read",
    ],
    support_operator: [
        "dashboard.read", "tenant.read", "service_config.read", "support.grant", "support.activate", "support.revoke", "audit.read", "health.read", "assistant.chat",
        // 客服只读商业状态，用于回答客户「我的服务什么时候到期」，但不能改任何商业事实。
        "plans.read", "subscriptions.read", "orders.read", "invoices.read", "usage.read",
    ],
    billing_operator: [
        "dashboard.read", "tenant.read", "audit.read", "assistant.chat",
        // 商业运营：订单、账单、线下收款、退款申请、对账。
        "catalog.read", "catalog.category_attributes.read", "plans.read", "subscriptions.read",
        "orders.read", "orders.create", "orders.confirm_payment", "orders.cancel",
        "invoices.read", "invoices.issue", "invoices.adjust", "billing_profiles.manage",
        "payments.read", "payments.confirm_offline",
        "refunds.read", "refunds.request",
        "usage.read", "usage.manage",
        "reconciliation.read", "reconciliation.run", "reconciliation.resolve",
        // 明确不授予：plans.version.manage / plans.publish / subscriptions.purchase /
        // subscriptions.change / usage.adjust / overrides.manage / refunds.approve /
        // refunds.execute / credential.* —— 商业运营不得改变功能权限、平台凭据或自批自退款。
    ],
    auditor: [
        "dashboard.read", "tenant.read", "tenant.applications.read", "service_config.read", "admin.read", "audit.read", "health.read", "usage.read", "assistant.chat",
        // 审计只读全部商业事实，不参与任何经营动作。
        "catalog.read", "plans.read", "subscriptions.read",
        "orders.read", "invoices.read", "payments.read", "refunds.read",
        "reconciliation.read",
    ],
};
/**
 * 商业运营（billing_operator）明确不得执行的动作：职责分离要求它不能改变功能权限、
 * 平台凭据或自批自退款。权限矩阵测试据此断言（ADR-0005 / RBAC-003）。
 */
export const billingOperatorForbidden = [
    "plans.version.manage", "plans.publish", "subscriptions.purchase", "subscriptions.change",
    "usage.adjust", "overrides.manage", "refunds.approve", "refunds.execute",
    "credential.write", "credential.rotate", "credential.revoke", "credential.rollback",
    "service_config.manage", "service_config.verify", "service_config.test",
];
/**
 * 高风险动作：默认必须满足职责分离（双人审批）。
 *
 * 规则：普通管理员请求人不得是自己的审批人；审批记录写入 approval_requests 并进入审计。
 * 租户生命周期允许 platform_owner 在明确确认影响与原因后直接执行，服务端仍按角色校验并写完整审计；
 * 该例外不扩展到资金、退款、凭据和发布等其他高风险动作。
 * 页面隐藏按钮只改善体验，服务端 requireAction + 审批校验才是安全边界（RBAC-002）。
 */
export const highRiskActions = [
    "plans.publish",
    "subscriptions.change",
    "orders.confirm_payment",
    "orders.create",
    "payments.confirm_offline",
    "invoices.adjust",
    "refunds.approve",
    "refunds.execute",
    "usage.adjust",
    "overrides.manage",
    "credential.rotate",
    "credential.revoke",
    "credential.rollback",
    "support.approve",
    "tenant.lifecycle",
    "admin.manage",
    "feature_flags.manage",
    "operations.manage",
];
export const highRiskActionSchema = z.enum(highRiskActions);
/** 需要双人审批的高风险动作说明，用于页面提示与审计展示。 */
export const highRiskActionLabels = {
    "plans.publish": "发布套餐版本",
    "subscriptions.change": "变更订阅",
    "orders.confirm_payment": "确认收款",
    "orders.create": "创建含合同优惠的订购单",
    "payments.confirm_offline": "线下确认收款",
    "invoices.adjust": "调整已出账单",
    "refunds.approve": "审批退款",
    "refunds.execute": "执行退款",
    "usage.adjust": "修正用量",
    "overrides.manage": "临时能力或额度例外",
    "credential.rotate": "轮换凭据",
    "credential.revoke": "吊销凭据",
    "credential.rollback": "回滚凭据版本",
    "support.approve": "审批受控代管",
    "tenant.lifecycle": "变更租户生命周期",
    "admin.manage": "变更平台管理员",
    "feature_flags.manage": "发布功能开关",
    "operations.manage": "运营事故与维护变更",
};
/**
 * 审批人角色：按职责分离约定，每个高风险动作只能由指定角色审批。
 * platform_owner 始终可以作为兜底审批人，但不能审批自己的请求。
 */
export const highRiskApproverRoles = {
    "plans.publish": ["platform_owner"],
    "subscriptions.change": ["platform_owner"],
    "orders.confirm_payment": ["platform_owner"],
    "orders.create": ["platform_owner"],
    "payments.confirm_offline": ["platform_owner"],
    "invoices.adjust": ["platform_owner"],
    "refunds.approve": ["platform_owner"],
    // 执行退款是资金动作，不由安全管理员参与，避免安全角色触碰资金链。
    "refunds.execute": ["platform_owner"],
    "usage.adjust": ["platform_owner"],
    // 临时例外由租户运营发起、平台负责人审批；安全管理员不介入经营口径的例外。
    "overrides.manage": ["platform_owner"],
    "credential.rotate": ["platform_owner", "security_admin"],
    "credential.revoke": ["platform_owner", "security_admin"],
    "credential.rollback": ["platform_owner", "security_admin"],
    "support.approve": ["platform_owner", "security_admin"],
    "tenant.lifecycle": ["platform_owner"],
    "admin.manage": ["platform_owner"],
    "feature_flags.manage": ["platform_owner", "security_admin"],
    "operations.manage": ["platform_owner", "security_admin"],
};
export const approvalOperationCatalog = [
    { operationType: "tenant.activate", label: "激活租户", category: "tenant", actionType: "tenant.lifecycle", targetType: "tenant", targetKind: "tenant", requiredPayloadFields: ["targetStatus"] },
    { operationType: "tenant.suspend", label: "暂停租户", category: "tenant", actionType: "tenant.lifecycle", targetType: "tenant", targetKind: "tenant", requiredPayloadFields: ["targetStatus"] },
    { operationType: "tenant.close", label: "关闭租户", category: "tenant", actionType: "tenant.lifecycle", targetType: "tenant", targetKind: "tenant", requiredPayloadFields: ["targetStatus"] },
    { operationType: "tenant.purge", label: "永久注销租户", category: "tenant", actionType: "tenant.lifecycle", targetType: "tenant", targetKind: "tenant", requiredPayloadFields: ["targetStatus"] },
    { operationType: "admin.create", label: "新增平台管理员", category: "admin", actionType: "admin.manage", targetType: "platform_admin", targetKind: "admin_email", requiredPayloadFields: ["op", "email", "role"] },
    { operationType: "admin.update", label: "变更管理员权限", category: "admin", actionType: "admin.manage", targetType: "platform_admin", targetKind: "admin", requiredPayloadFields: ["op", "role", "status"] },
    { operationType: "admin.delete", label: "删除平台管理员", category: "admin", actionType: "admin.manage", targetType: "platform_admin", targetKind: "admin", requiredPayloadFields: ["op"] },
    { operationType: "plan.publish", label: "发布套餐版本", category: "commercial", actionType: "plans.publish", targetType: "plan_version", targetKind: "plan_version", requiredPayloadFields: ["versionId", "acknowledgedImpactTenantCount"] },
    { operationType: "addon.publish", label: "发布加购版本", category: "commercial", actionType: "plans.publish", targetType: "addon_version", targetKind: "addon_version", requiredPayloadFields: ["addonVersionId"] },
    { operationType: "plan.force_migration", label: "套餐强制迁移", category: "commercial", actionType: "subscriptions.change", targetType: "subscription_change", targetKind: "subscription_change", requiredPayloadFields: ["changeId", "changeType", "fromPlanVersionId", "toPlanVersionId", "affectedTenantCount"] },
    { operationType: "subscription.change", label: "执行订阅变更", category: "commercial", actionType: "subscriptions.change", targetType: "subscription_change", targetKind: "subscription_change", requiredPayloadFields: ["changeId", "targetChangeId", "changeType", "fromPlanVersionId", "toPlanVersionId", "affectedTenantCount"] },
    { operationType: "discount_rule.approve", label: "批准合同优惠", category: "billing", actionType: "orders.create", targetType: "commercial_discount_rule", targetKind: "discount_rule", requiredPayloadFields: ["discountRuleId", "tenantId", "planVersionId", "addonVersionId", "billingInterval", "currency", "discountAmountMinor", "startsAt", "endsAt", "maxRedemptions"] },
    { operationType: "payment.offline_confirm", label: "线下确认收款", category: "billing", actionType: "payments.confirm_offline", targetType: "purchase_order", targetKind: "purchase_order", requiredPayloadFields: ["orderId", "amountMinor", "confirmationReference"] },
    { operationType: "invoice.adjust", label: "调整账单", category: "billing", actionType: "invoices.adjust", targetType: "invoice", targetKind: "invoice", requiredPayloadFields: ["invoiceId", "lineType", "amountMinor"] },
    { operationType: "refund.approve", label: "审批退款", category: "billing", actionType: "refunds.approve", targetType: "refund_transaction", targetKind: "refund", requiredPayloadFields: ["refundId", "decision", "amountMinor", "subscriptionHandling"] },
    { operationType: "refund.execute", label: "执行退款", category: "billing", actionType: "refunds.execute", targetType: "refund_transaction", targetKind: "refund", requiredPayloadFields: ["refundId", "amountMinor", "subscriptionHandling"] },
    { operationType: "usage.adjust", label: "修正用量", category: "billing", actionType: "usage.adjust", targetType: "usage_adjustment", targetKind: "usage_meter", requiredPayloadFields: ["tenantId", "meterId", "periodStart", "periodEnd", "quantityDelta"] },
    { operationType: "quota_override.create", label: "创建临时额度例外", category: "billing", actionType: "overrides.manage", targetType: "quota_override", targetKind: "usage_meter", requiredPayloadFields: ["tenantId", "meterId", "unlimited", "limitQuantity", "softLimitPercent", "startsAt", "endsAt"] },
    { operationType: "credential.rotate", label: "轮换凭据", category: "security", actionType: "credential.rotate", targetType: "credential", targetKind: "credential", requiredPayloadFields: ["op", "credentialId", "scope", "tenantId", "provider", "submittedSecretFields", "secretPayloadDigest"] },
    { operationType: "credential.revoke", label: "吊销凭据", category: "security", actionType: "credential.revoke", targetType: "credential", targetKind: "credential", requiredPayloadFields: ["op"] },
    { operationType: "credential.rollback", label: "回滚凭据版本", category: "security", actionType: "credential.rollback", targetType: "credential", targetKind: "credential", requiredPayloadFields: ["op", "fromVersion", "toVersion"] },
    { operationType: "support.approve", label: "审批受控代管", category: "security", actionType: "support.approve", targetType: "support_grant", targetKind: "support_grant", requiredPayloadFields: ["supportGrantId", "operatorId", "expiresAt"] },
    { operationType: "feature_flag.adjust", label: "调整功能开关", category: "operations", actionType: "feature_flags.manage", targetType: "feature_flag", targetKind: "feature_flag", requiredPayloadFields: ["operation", "enabled", "rolloutPercent", "previousRolloutPercent"] },
    { operationType: "feature_flag.full_publish", label: "功能开关 100% 发布", category: "operations", actionType: "feature_flags.manage", targetType: "feature_flag", targetKind: "feature_flag", requiredPayloadFields: ["operation", "enabled", "rolloutPercent", "previousRolloutPercent", "impactScope", "acknowledgedFullReleaseImpact"] },
    { operationType: "incident.open", label: "登记运营事故", category: "operations", actionType: "operations.manage", targetType: "platform_incident", targetKind: "incident_scope", requiredPayloadFields: ["scope", "ownerSelectionToken", "timeline", "recoveryPlan", "postmortem", "idempotencyKey"] },
    { operationType: "incident.transition", label: "更新事故处置状态", category: "operations", actionType: "operations.manage", targetType: "platform_incident", targetKind: "incident", requiredPayloadFields: ["timeline", "ownerSelectionToken", "idempotencyKey"] },
    { operationType: "incident.resolve_with_recovery", label: "事故恢复并进入复盘", category: "operations", actionType: "operations.manage", targetType: "platform_incident", targetKind: "incident", requiredPayloadFields: ["recoveryEvidence", "postmortem", "idempotencyKey"] },
    { operationType: "maintenance.schedule", label: "计划维护窗口", category: "operations", actionType: "operations.manage", targetType: "maintenance_window", targetKind: "maintenance_scope", requiredPayloadFields: ["window", "scope", "notificationPlan", "cancellation", "earlyEnd", "idempotencyKey"] },
    { operationType: "maintenance.cancel", label: "取消维护窗口", category: "operations", actionType: "operations.manage", targetType: "maintenance_window", targetKind: "maintenance_window", requiredPayloadFields: ["reason", "notificationPlan", "idempotencyKey"] },
    { operationType: "maintenance.end_early", label: "提前结束维护窗口", category: "operations", actionType: "operations.manage", targetType: "maintenance_window", targetKind: "maintenance_window", requiredPayloadFields: ["recoveryEvidence", "notificationPlan", "idempotencyKey"] },
    { operationType: "feature_flag.schedule_change", label: "计划功能开关变更", category: "operations", actionType: "feature_flags.manage", targetType: "feature_flag", targetKind: "feature_flag", requiredPayloadFields: ["catalogKey", "environment", "scope", "impactPreviewToken", "plannedAt", "autoExpireAt", "rollbackPlan"] },
    { operationType: "feature_flag.rollback", label: "回滚功能开关", category: "operations", actionType: "feature_flags.manage", targetType: "feature_flag", targetKind: "feature_flag", requiredPayloadFields: ["catalogKey", "environment", "rollbackPlan", "idempotencyKey"] },
];
export const approvalOperationTypes = approvalOperationCatalog.map((item) => item.operationType);
export const approvalOperationTypeSchema = z.enum(approvalOperationTypes);
export const tenantStatuses = ["provisioning", "active", "suspended", "archived", "purged"];
export const tenantStatusSchema = z.enum(tenantStatuses);
export const tenantSchema = z.object({
    id: z.string().uuid(),
    key: z.string().regex(/^[a-z0-9][a-z0-9-]{2,62}$/),
    name: z.string().min(2).max(120),
    status: tenantStatusSchema,
    planKey: z.string().min(1).max(80),
    region: z.string().min(2).max(40),
    isolationReadiness: z.number().int().min(0).max(100),
    /** 暂停/关闭原因（业务语言），360 概览与台账风险提示使用。 */
    suspensionReason: z.string().nullable(),
    /** 平台侧负责人（管理员），用于台账筛选与责任到人。 */
    ownerAdminId: z.string().uuid().nullable(),
    ownerName: z.string().nullable(),
    /** 企业联系人（注册申请或人工创建时登记）。 */
    contactName: z.string().nullable(),
    contactEmail: z.string().email().nullable(),
    contactPhone: z.string().nullable(),
    /** 技术联系人（可选，供平台侧技术对接使用）。 */
    techContactName: z.string().nullable(),
    techContactEmail: z.string().email().nullable(),
    techContactPhone: z.string().nullable(),
    purgedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * 外部 HTTP DTO（POST /api/tenants 请求体）：普通操作者创建租户的边界。
 * strict 模式：任何技术字段（key / desiredTenantKey / tenantId 等）都会被明确拒绝（400），
 * 绝不允许「前端隐藏、接口继续接受」。租户内部编号只由服务端生成。
 */
export const tenantCreateSchema = z.strictObject({
    name: tenantSchema.shape.name,
    /**
     * 初始套餐必须指向真实、可售（已发布且产品在售）的不可变套餐版本；
     * 不存在真实订阅事实时服务端明确阻断，绝不回退为只写 planKey。
     */
    planVersionId: z.string().uuid(),
    region: z.string().min(2).max(40).default("cn-east"),
    ownerAdminId: z.string().uuid().optional(),
    contactName: z.string().trim().min(2).max(80).optional(),
    contactEmail: z.string().trim().email().max(254).transform((value) => value.toLowerCase()).optional(),
    contactPhone: z.string().trim().regex(/^1\d{10}$/, "请输入有效的中国大陆手机号").optional(),
    techContactName: z.string().trim().min(2).max(80).optional(),
    techContactEmail: z.string().trim().email().max(254).transform((value) => value.toLowerCase()).optional(),
    techContactPhone: z.string().trim().regex(/^1\d{10}$/, "请输入有效的中国大陆手机号").optional(),
});
/**
 * 可售套餐版本目录（新建租户向导 / 申请复核使用）：
 * 只返回真实、可售（产品在售 + 版本已发布）的不可变套餐版本，展示业务名称。
 */
export const tenantPlanOptionSchema = z.object({
    /** 套餐版本标识：创建租户 / 复核接入时提交。 */
    planVersionId: z.string().uuid(),
    /** 套餐业务名称（如「基础版」），普通操作者只看到它。 */
    planName: z.string().min(1).max(120),
    /** 已发布版本号，界面上表达为「已发布第 N 版」。 */
    version: z.number().int().min(1),
    /** 版本包含的功能数量（业务语言：可用功能数）。 */
    capabilitiesCount: z.number().int().min(0),
    /** 版本包含的资源额度项数量。 */
    meterLimitsCount: z.number().int().min(0),
    /** 套餐说明（业务语言）。 */
    description: z.string(),
});
/** 租户档案元数据维护（负责人/联系人）；生命周期走 transition + 审批。 */
export const tenantProfileUpdateSchema = z.object({
    reason: z.string().trim().min(8).max(500),
    ownerAdminId: z.string().uuid().nullable().optional(),
    contactName: z.string().trim().min(2).max(80).nullable().optional(),
    contactEmail: z.string().trim().email().max(254).nullable().optional(),
    contactPhone: z.string().trim().max(32).nullable().optional(),
    techContactName: z.string().trim().min(2).max(80).nullable().optional(),
    techContactEmail: z.string().trim().email().max(254).nullable().optional(),
    techContactPhone: z.string().trim().max(32).nullable().optional(),
});
/** 新建租户前的真实预检：企业/管理员/域名重复与激活安全条件（CP-TENANT-004）。 */
export const tenantPrecheckSchema = z.object({
    name: tenantSchema.shape.name,
    contactEmail: z.string().trim().email().max(254).transform((value) => value.toLowerCase()).optional(),
    contactPhone: z.string().trim().regex(/^1\d{10}$/, "请输入有效的中国大陆手机号").optional(),
});
export const tenantApplicationStatuses = ["pending", "approved", "rejected", "provisioned", "merged"];
export const tenantApplicationStatusSchema = z.enum(tenantApplicationStatuses);
/**
 * 自动开通阶段（CP-TENANT-005）：申请从资料确认到完成的服务端事实阶段。
 * 每个阶段都对应真实数据库事实（订阅、授权、额度），不允许只改文案。
 */
export const tenantOnboardingStages = [
    "profile_confirmed",
    "subscription_established",
    "entitlements_projected",
    "quotas_established",
    "safety_checked",
    "awaiting_activation",
    "completed",
];
export const tenantOnboardingStageSchema = z.enum(tenantOnboardingStages);
/** 阶段的操作者中文名（普通界面只出现业务语言，不出现内部标识）。 */
export const tenantOnboardingStageLabels = {
    profile_confirmed: "资料已确认",
    subscription_established: "订阅已建立",
    entitlements_projected: "使用资格已发放",
    quotas_established: "额度已建立",
    safety_checked: "安全检查已通过",
    awaiting_activation: "等待激活",
    completed: "开通完成",
};
/**
 * 公开申请 HTTP DTO（POST /api/public/tenant-applications 请求体）。
 * strict 模式：desiredTenantKey 等内部字段被明确拒绝（400），
 * 租户内部标识只由服务端生成；website 是蜜罐字段（机器人填入即整体拒绝）。
 */
export const tenantApplicationCreateSchema = z.strictObject({
    companyName: z.string().trim().min(2).max(120),
    contactName: z.string().trim().min(2).max(80),
    email: z.string().trim().email().max(254).transform((value) => value.toLowerCase()),
    phone: z.string().trim().regex(/^1\d{10}$/, "请输入有效的中国大陆手机号"),
    acceptedTerms: z.literal(true),
    website: z.string().max(0).optional(),
});
export const tenantApplicationReviewSchema = z.discriminatedUnion("decision", [
    z.object({
        decision: z.literal("approve"),
        note: z.string().trim().min(8).max(500),
        /**
         * 批准接入的初始套餐版本：必须指向真实、可售（产品在售 + 版本已发布）的
         * 不可变套餐版本；批准即在同一事务内建立订阅事实，绝不只写 planKey。
         */
        planVersionId: z.string().uuid(),
        region: z.string().min(2).max(40).default("cn-east"),
    }),
    z.object({ decision: z.literal("reject"), note: z.string().trim().min(8).max(500) }),
]);
export const tenantApplicationSchema = z.object({
    id: z.string().uuid(),
    companyName: z.string(),
    contactName: z.string(),
    email: z.string().email(),
    phoneMasked: z.string(),
    desiredTenantKey: tenantSchema.shape.key,
    status: tenantApplicationStatusSchema,
    reviewNote: z.string().nullable(),
    tenantId: z.string().uuid().nullable(),
    activationExpiresAt: z.string().datetime().nullable(),
    activationConsumedAt: z.string().datetime().nullable(),
    /** 自动接入引擎已重试次数与最近一次失败原因（CP-TENANT-005）。 */
    retryCount: z.number().int().min(0),
    lastFailureReason: z.string().nullable(),
    /** 自动开通阶段机（CP-TENANT-005）：当前阶段与最近失败阶段，均与数据库事实同步。 */
    onboardingStage: tenantOnboardingStageSchema,
    onboardingFailedStage: tenantOnboardingStageSchema.nullable(),
    onboardingStageUpdatedAt: z.string().datetime().nullable(),
    /** 转交处理人姓名（服务端 join 管理员，无则空）。 */
    assignedToName: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** 申请队列处置动作（CP-TENANT-008）：合并重复 / 转交。 */
export const tenantApplicationMergeSchema = z.object({
    mergeIntoApplicationId: z.string().uuid(),
    note: z.string().trim().min(8).max(500),
});
export const tenantApplicationTransferSchema = z.object({
    toAdminId: z.string().uuid(),
    note: z.string().trim().min(8).max(500),
});
export const tenantTransitionSchema = z.object({
    status: tenantStatusSchema,
    reason: z.string().trim().min(8).max(500),
});
export const readinessCheckKeys = [
    "database_rows", "tenant_uniqueness", "background_jobs", "websocket_events",
    "credentials_webhooks", "storage_cache_ai", "resource_ownership", "cross_tenant_tests",
];
export const readinessCheckKeySchema = z.enum(readinessCheckKeys);
export const readinessCheckUpdateSchema = z.object({
    checkKey: readinessCheckKeySchema,
    status: z.enum(["pending", "passed", "failed"]),
    evidence: z.string().trim().min(12).max(2_000),
});
export const entitlementSchema = z.object({
    featureKey: z.string().min(1).max(120),
    licensed: z.boolean(),
    enabled: z.boolean(),
    expiresAt: z.string().datetime().nullable(),
});
export const entitlementUpdateSchema = z.object({
    featureKey: z.string().min(1).max(120),
    licensed: z.boolean(),
    enabled: z.boolean(),
    expiresAt: z.string().datetime().nullable().default(null),
}).refine((value) => value.licensed || !value.enabled, {
    message: "未授权功能不能启用",
    path: ["enabled"],
});
export const quotaUpdateSchema = z.object({
    resourceKey: z.string().regex(/^[a-z0-9_.-]+$/).max(100),
    softLimit: z.number().int().nonnegative(),
    hardLimit: z.number().int().positive(),
    period: z.enum(["day", "month", "total"]),
}).refine((value) => value.softLimit <= value.hardLimit, {
    message: "软限制不能高于硬限制",
    path: ["softLimit"],
});
export const credentialScopes = ["platform", "tenant"];
export const credentialScopeSchema = z.enum(credentialScopes);
export const credentialStatuses = ["active", "rotating", "revoked", "expired"];
export const credentialStatusSchema = z.enum(credentialStatuses);
export const credentialMetadataSchema = z.object({
    id: z.string().uuid(),
    scope: credentialScopeSchema,
    tenantId: z.string().uuid().nullable(),
    provider: z.string().min(1).max(80),
    name: z.string().min(1).max(120),
    status: credentialStatusSchema,
    activeVersion: z.number().int().positive(),
    fingerprint: z.string().length(16),
    expiresAt: z.string().datetime().nullable(),
    lastUsedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const credentialCreateSchema = z.object({
    scope: credentialScopeSchema,
    tenantId: z.string().uuid().nullable(),
    provider: z.string().regex(/^[a-z0-9_-]+$/i).max(80),
    name: z.string().trim().min(2).max(120),
    secret: z.string().min(8).max(32_768),
    expiresAt: z.string().datetime().nullable().default(null),
}).superRefine((value, ctx) => {
    if (value.scope === "tenant" && !value.tenantId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tenantId"], message: "租户凭据必须指定租户" });
    }
    if (value.scope === "platform" && value.tenantId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["tenantId"], message: "平台凭据不能绑定租户" });
    }
});
export const credentialRotateSchema = z.object({
    secret: z.string().min(8).max(32_768),
    expiresAt: z.string().datetime().nullable().default(null),
    reason: z.string().trim().min(8).max(500),
});
export const credentialRollbackSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
});
export const revokeSchema = z.object({ reason: z.string().trim().min(8).max(500) });
export const PLATFORM_ADMIN_PASSWORD_MIN_LENGTH = 10;
export const platformAdminPasswordSchema = z.string()
    .min(PLATFORM_ADMIN_PASSWORD_MIN_LENGTH, `密码至少 ${PLATFORM_ADMIN_PASSWORD_MIN_LENGTH} 位`)
    .max(256, "密码不能超过 256 位")
    .regex(/[A-Z]/, "密码必须包含大写字母")
    .regex(/[a-z]/, "密码必须包含小写字母")
    .regex(/[0-9]/, "密码必须包含数字");
export const loginSchema = z.object({
    email: z.string().email().max(320).transform((value) => value.toLowerCase()),
    password: z.string().min(PLATFORM_ADMIN_PASSWORD_MIN_LENGTH).max(256),
    totpCode: z.string().regex(/^\d{6}$/).optional(),
});
const securityIdempotencyKeySchema = z.string().trim().min(8).max(160);
const approvalIdSchema = z.string().uuid();
const approvalTargetDigestSchema = z.string().trim().min(8).max(300);
const securityBusinessReasonSchema = z.string().trim().min(8).max(500);
const securityAdminStatusSchema = z.enum(["active", "suspended"]);
const approvalBoundMutationSchema = z.object({
    approvalId: approvalIdSchema,
    approvedTargetDigest: approvalTargetDigestSchema,
    reason: securityBusinessReasonSchema,
    idempotencyKey: securityIdempotencyKeySchema,
}).strict();
export const adminDirectoryQuerySchema = z.object({
    role: platformRoleSchema.optional(),
    status: securityAdminStatusSchema.optional(),
    q: z.string().trim().min(1).max(120).optional(),
    mfa: z.enum(["enabled", "disabled"]).optional(),
    lastLogin: z.enum(["within_7d", "within_30d", "inactive_30d", "never"]).optional(),
    risk: z.enum(["low", "medium", "high"]).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().trim().min(1).max(300).optional(),
}).strict();
export const adminDirectoryItemSchema = z.object({
    id: z.string().min(1).max(160),
    email: z.string().email().max(320),
    displayName: z.string().trim().min(1).max(80),
    role: platformRoleSchema,
    roleLabel: z.string().trim().min(1).max(40),
    status: securityAdminStatusSchema,
    statusLabel: z.string().trim().min(1).max(40),
    mfa: z.object({
        enabled: z.boolean(),
        label: z.string().trim().min(1).max(40),
    }).strict(),
    lastLoginLabel: z.string().trim().min(1).max(80),
    risk: z.enum(["low", "medium", "high"]),
    riskLabel: z.string().trim().min(1).max(40),
    capabilities: z.array(z.string().trim().min(1).max(80)).max(24),
}).strict();
export const adminCreateSchema = loginSchema.extend({
    password: platformAdminPasswordSchema,
    displayName: z.string().trim().min(2).max(80),
    role: platformRoleSchema,
}).merge(approvalBoundMutationSchema);
export const adminCreateApprovalRequestSchema = adminCreateSchema;
export const adminUpdateSchema = z.object({
    role: platformRoleSchema.optional(),
    status: securityAdminStatusSchema.optional(),
}).strict().merge(approvalBoundMutationSchema)
    .refine((value) => value.role !== undefined || value.status !== undefined, { message: "必须修改角色或状态" });
export const adminUpdateApprovalRequestSchema = adminUpdateSchema;
export const adminDeleteSchema = approvalBoundMutationSchema;
export const adminDeleteApprovalRequestSchema = adminDeleteSchema;
export const accountSecurityCenterSchema = z.object({
    profile: z.object({
        email: z.string().email().max(320),
        displayName: z.string().trim().min(1).max(80),
        role: platformRoleSchema,
        roleLabel: z.string().trim().min(1).max(40),
        status: securityAdminStatusSchema,
        statusLabel: z.string().trim().min(1).max(40),
    }).strict(),
    mfa: z.object({
        enabled: z.boolean(),
        enrolledAt: z.string().datetime().nullable(),
        recoveryCodesRemaining: z.number().int().nonnegative().nullable(),
    }).strict(),
    sessions: z.object({
        activeCount: z.number().int().nonnegative(),
        currentSessionLabel: z.string().trim().min(1).max(80),
    }).strict(),
    loginHistory: z.object({
        recentFailureCount: z.number().int().nonnegative(),
        lastSuccessfulLoginLabel: z.string().trim().min(1).max(80),
    }).strict(),
    trustedDevices: z.object({
        total: z.number().int().nonnegative(),
        staleCount: z.number().int().nonnegative(),
    }).strict(),
}).strict();
export const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1).max(256),
    newPassword: platformAdminPasswordSchema,
    reason: securityBusinessReasonSchema.optional(),
    idempotencyKey: securityIdempotencyKeySchema,
}).strict();
export const mfaEnrollmentSchema = z.object({
    action: z.enum(["start", "verify", "disable"]),
    totpCode: z.string().regex(/^\d{6}$/).optional(),
    recoveryCode: z.string().trim().min(8).max(80).optional(),
    idempotencyKey: securityIdempotencyKeySchema,
}).strict();
export const activeSessionListSchema = z.object({
    items: z.array(z.object({
        sessionLabel: z.string().trim().min(1).max(120),
        deviceLabel: z.string().trim().min(1).max(120),
        ipLocationLabel: z.string().trim().min(1).max(120),
        lastSeenAt: z.string().datetime(),
        current: z.boolean(),
        revokeToken: z.string().trim().min(8).max(300).optional(),
    }).strict()),
}).strict();
export const loginHistoryListSchema = z.object({
    items: z.array(z.object({
        occurredAt: z.string().datetime(),
        result: z.enum(["success", "failed", "blocked"]),
        resultLabel: z.string().trim().min(1).max(40),
        ipLocationLabel: z.string().trim().min(1).max(120),
        deviceLabel: z.string().trim().min(1).max(120),
    }).strict()),
}).strict();
export const trustedDeviceListSchema = z.object({
    items: z.array(z.object({
        deviceLabel: z.string().trim().min(1).max(120),
        trustLabel: z.string().trim().min(1).max(80),
        lastUsedAt: z.string().datetime().nullable(),
        revokeToken: z.string().trim().min(8).max(300),
    }).strict()),
}).strict();
export const supportGrantCreateSchema = z.object({
    tenantSelectionToken: z.string().regex(/^opssel_[A-Za-z0-9_-]+$/).max(5000),
    operatorSelectionToken: z.string().regex(/^opssel_[A-Za-z0-9_-]+$/).max(5000),
    accessProfile: z.enum(["diagnostics_read_only", "configuration_review", "incident_response"]),
    reason: z.string().trim().min(12).max(500),
    durationMinutes: z.number().int().min(5).max(120),
    idempotencyKey: securityIdempotencyKeySchema,
}).strict();
export const supportAccessRequestSchema = supportGrantCreateSchema;
export const supportAccessSessionSchema = z.object({
    tenantLabel: z.string().trim().min(1).max(120),
    operatorLabel: z.string().trim().min(1).max(80),
    permissionSummary: z.string().trim().min(1).max(120),
    remainingSeconds: z.number().int().positive().max(7 * 24 * 3600),
}).strict();
export const supportAccessActivationSchema = z.object({
    oneTimeTicket: z.string().trim().min(16).max(5000),
    session: supportAccessSessionSchema,
}).strict();
export const auditListQuerySchema = z.object({
    keyword: z.string().trim().min(1).max(160).optional(),
    actor: z.string().trim().min(1).max(120).optional(),
    object: z.string().trim().min(1).max(160).optional(),
    tenant: z.string().trim().min(1).max(160).optional(),
    risk: z.enum(["low", "medium", "high"]).optional(),
    result: z.enum(["success", "blocked", "failed"]).optional(),
    verification: z.enum(["valid", "failed", "unchecked"]).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(50),
}).strict();
export const auditListItemSchema = z.object({
    sentence: z.string().trim().min(8).max(500),
    objectLabel: z.string().trim().min(1).max(120),
    actorLabel: z.string().trim().min(1).max(120),
    tenantLabel: z.string().trim().min(1).max(120),
    occurredAt: z.string().datetime(),
    risk: z.enum(["low", "medium", "high"]),
    result: z.enum(["success", "blocked", "failed"]),
    verificationStatus: z.enum(["valid", "failed", "unchecked"]),
    evidenceToken: z.string().trim().min(8).max(300),
}).strict();
export const auditExportRequestSchema = auditListQuerySchema.extend({
    format: z.enum(["csv", "jsonl"]).default("csv"),
    idempotencyKey: securityIdempotencyKeySchema,
}).strict();
export const auditVerifyResultSchema = z.object({
    verificationStatus: z.enum(["valid", "failed"]),
    checkedAt: z.string().datetime(),
    incidentToken: z.string().trim().min(8).max(300).nullable(),
    failedSequence: z.number().int().nonnegative().nullable(),
}).strict();
const machineActionSchema = z.enum(["runtime.policy.read", "runtime.usage.write", "runtime.connection.health.write", "runtime.platform_config.read", "runtime.ai_config.read", "runtime.print_agent_policy.read", "runtime.quota_policy.read", "runtime.sms_config.read", "runtime.web_wechat_login_config.read"]);
export const serviceAccountPurposes = ["erp_runtime", "print_agent", "integration_worker"];
export const serviceAccountPurposeSchema = z.enum(serviceAccountPurposes);
/** 用户只选择用途；账号名与机器动作由服务端预设生成，不能由页面扩大权限。 */
export const serviceAccountPurposePresets = {
    erp_runtime: {
        label: "ERP 业务运行",
        namePrefix: "ERP 运行账号",
        allowedActions: ["runtime.policy.read", "runtime.usage.write", "runtime.platform_config.read", "runtime.ai_config.read", "runtime.print_agent_policy.read", "runtime.quota_policy.read", "runtime.sms_config.read", "runtime.web_wechat_login_config.read"],
    },
    print_agent: {
        label: "打印组件",
        namePrefix: "打印组件账号",
        allowedActions: ["runtime.print_agent_policy.read"],
    },
    integration_worker: {
        label: "连接健康检查",
        namePrefix: "连接检查账号",
        allowedActions: ["runtime.policy.read", "runtime.connection.health.write"],
    },
};
/**
 * 平台 API 配额策略（控制面集中管理 → /runtime/quota-policy 密封下发 ERP）。
 * 字段与 ERP app-final/api/lib/quotaPolicy.ts 的 QuotaPolicy 一致。
 */
export const quotaPolicyValueSchema = z.object({
    qpsLimit: z.number().int().min(1).max(10_000),
    dailyCallBudget: z.number().int().min(0).max(100_000_000),
    warningThreshold: z.number().min(0).max(1),
    backoffCapMs: z.number().int().min(60_000).max(24 * 3600_000),
});
export const quotaPolicyUpsertSchema = z.object({
    platforms: z.record(quotaPolicyValueSchema),
});
const printAgentOriginPolicyBaseSchema = z.object({
    allowedOrigins: z.array(z.string().trim().url()).min(1).max(20),
});
function validatePrintAgentOriginPolicy(value, ctx) {
    const normalized = new Set();
    for (const [index, raw] of value.allowedOrigins.entries()) {
        const url = new URL(raw);
        const localHttp = url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname);
        if (url.username || url.password || url.pathname !== "/" || url.search || url.hash || (url.protocol !== "https:" && !localHttp)) {
            ctx.addIssue({ code: "custom", path: ["allowedOrigins", index], message: "必须是精确 HTTPS Origin；仅 localhost 可使用 HTTP" });
        }
        normalized.add(url.origin);
    }
    if (normalized.size !== value.allowedOrigins.length)
        ctx.addIssue({ code: "custom", path: ["allowedOrigins"], message: "Origin 不能重复" });
    if (!normalized.has("https://erp.ekaiwork.com"))
        ctx.addIssue({ code: "custom", path: ["allowedOrigins"], message: "必须保留生产 ERP Origin：https://erp.ekaiwork.com" });
}
export const printAgentOriginPolicyUpdateSchema = printAgentOriginPolicyBaseSchema.strict().superRefine(validatePrintAgentOriginPolicy);
export const printAgentOriginPolicyProbeSchema = printAgentOriginPolicyBaseSchema.extend({
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict().superRefine(validatePrintAgentOriginPolicy);
export const printAgentOriginProbeStatusSchema = z.enum(["reachable", "failed", "development_only"]);
export const aiProviderSchema = z.enum(["qwen", "deepseek", "kimi", "zhipu", "mimo", "custom_openai_compatible"]);
export const aiModelDiscoverySchema = z.object({
    provider: aiProviderSchema,
    apiKey: z.string().trim().min(8).max(32_768),
    baseUrl: z.string().trim().url().max(2_000),
}).strict();
export const aiModelDiscoveryResponseSchema = z.object({
    models: z.array(z.object({
        id: z.string().trim().min(1).max(160),
        name: z.string().trim().min(1).max(160),
        capabilities: z.array(z.enum(["chat", "vision", "tool_call", "embedding", "rerank"])).max(5),
        capabilitySource: z.enum(["provider", "unreported"]),
    }).strict()).max(500),
}).strict();
export const serviceConfigStatusSchema = z.enum(["missing", "configured", "verified", "degraded", "blocked"]);
export const serviceConfigVerificationStatusSchema = z.enum(["success", "failed", "unsupported"]);
export const serviceConfigCallbackSubjectSchema = z.enum(["oauth_callback", "message_callback", "sms_callback", "web_wechat_login_callback"]);
export const serviceConfigCallbackVerifySchema = z.object({
    provider: z.string().trim().min(2).max(80),
    subject: serviceConfigCallbackSubjectSchema,
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const serviceConfigEnableSchema = z.object({
    service: z.enum(["platform_application", "ai", "sms", "web_wechat_login"]),
    provider: z.string().trim().min(2).max(80),
    model: z.string().trim().min(1).max(160).nullable().default(null),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict().superRefine((value, ctx) => {
    if (value.service === "ai" && value.provider === "zhipu" && value.model === "glm-ocr") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["service"], message: "GLM-OCR 必须使用独立 OCR 配置链路" });
    }
});
export const aiModelCapabilities = ["chat", "vision", "tool_call", "embedding", "rerank"];
export const aiModelCapabilitySchema = z.enum(aiModelCapabilities);
export const aiModelCatalogUpsertSchema = z.object({
    provider: aiProviderSchema,
    model: z.string().trim().min(1).max(160),
    displayName: z.string().trim().min(1).max(160),
    status: z.enum(["active", "disabled", "retired"]).default("active"),
    capabilities: z.array(aiModelCapabilitySchema).default(["chat"]),
    contextWindow: z.number().int().positive().max(10_000_000).nullable().default(null),
    maxOutputTokens: z.number().int().positive().max(10_000_000).nullable().default(null),
    currency: z.enum(["CNY", "USD"]).default("CNY"),
    inputTokenPriceMicros: z.number().int().nonnegative().max(2_147_483_647).default(0),
    outputTokenPriceMicros: z.number().int().nonnegative().max(2_147_483_647).default(0),
    dailyTokenBudget: z.number().int().positive().max(2_147_483_647).nullable().default(null),
    dailyCostBudgetMinor: z.number().int().positive().max(2_147_483_647).nullable().default(null),
    monthlyCostBudgetMinor: z.number().int().positive().max(2_147_483_647).nullable().default(null),
    fallbackProvider: aiProviderSchema.nullable().default(null),
    fallbackModel: z.string().trim().min(1).max(160).nullable().default(null),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict().superRefine((value, ctx) => {
    if (value.provider === "zhipu" && value.model === "glm-ocr") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["model"], message: "GLM-OCR 是专用 OCR 服务，不能登记到聊天模型目录" });
    }
    if ((value.fallbackProvider && !value.fallbackModel) || (!value.fallbackProvider && value.fallbackModel)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fallbackModel"], message: "降级服务商和降级模型必须同时填写" });
    }
});
export const aiConnectivityTestSchema = z.object({
    provider: aiProviderSchema,
    model: z.string().trim().min(1).max(160),
    prompt: z.string().trim().min(1).max(500).default("请回复 OK，用于控制面连通测试。"),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict().superRefine((value, ctx) => {
    if (value.provider === "zhipu" && value.model === "glm-ocr") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["model"], message: "GLM-OCR 必须使用独立 OCR 连通测试" });
    }
});
/** GLM-OCR is a document recognition runtime, not a chat model catalog entry. */
export const aiOcrBudgetUpsertSchema = z.object({
    maxFilesPerDay: z.number().int().positive().max(2_147_483_647),
    maxPagesPerDay: z.number().int().positive().max(2_147_483_647),
    dailyCostBudgetMinor: z.number().int().positive().max(2_147_483_647),
    currency: z.literal("CNY").default("CNY"),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const aiOcrConnectivityTestSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const aiOcrEnableSchema = z.object({
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const smsSendTestSchema = z.object({
    phone: z.string().trim().regex(/^\+?[0-9]{8,15}$/),
    templateParams: z.record(z.string().trim().min(1).max(64), z.string().trim().max(200)).default({}),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const aiRoutingDefaultUpdateSchema = z.object({
    provider: aiProviderSchema,
});
export const serviceAccountCreateSchema = z.object({
    tenantId: z.string().uuid(),
    purpose: serviceAccountPurposeSchema,
    expiresAt: z.string().datetime().nullable().default(null),
}).strict();
export const serviceAccountRotationStartSchema = z.object({
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const serviceAccountRotationVerifySchema = z.object({
    version: z.number().int().positive(),
    clientSecret: z.string().min(24).max(512),
}).strict();
export const serviceAccountRotationActivateSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const serviceAccountSecretRevokeSchema = serviceAccountRotationActivateSchema;
export const serviceAccountActionsUpdateSchema = z.object({
    allowedActions: z.array(machineActionSchema).min(1),
    reason: z.string().trim().min(8).max(500),
});
export const planUpsertSchema = z.object({
    key: z.string().regex(/^[a-z0-9][a-z0-9_.-]{1,79}$/),
    name: z.string().trim().min(2).max(120),
    status: z.enum(["draft", "active", "retired"]),
    version: z.number().int().positive(),
    description: z.string().trim().max(1000).default(""),
});
export const subscriptionUpsertSchema = z.object({
    planId: z.string().uuid(),
    status: z.enum(["trialing", "active", "past_due", "canceled"]),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().nullable().default(null),
    billingCustomerRef: z.string().trim().max(160).nullable().default(null),
});
export const usageMeterUpsertSchema = z.object({
    featureKey: z.string().regex(/^[a-z0-9_.-]+$/).max(120),
    displayName: z.string().trim().min(2).max(120),
    unit: z.string().trim().min(1).max(40),
    aggregation: z.enum(["sum", "count"]),
    decimalScale: z.number().int().min(0).max(6).default(0),
});
export const usageEventSchema = z.object({
    meterKey: z.string().regex(/^[a-z0-9][a-z0-9_.:-]{1,119}$/),
    idempotencyKey: z.string().min(8).max(160),
    /**
     * 用量数量使用十进制整数字符串，配合 decimalScale 表达小数。
     * 例如 quantity="12345" + decimalScale=2 表示 123.45，避免 JS number 精度损失。
     */
    quantity: z.string().regex(/^(0|[1-9][0-9]{0,17})$/).default("1"),
    decimalScale: z.number().int().min(0).max(6).default(0),
    sourceEventId: z.string().trim().min(1).max(160).nullable().default(null),
    occurredAt: z.string().datetime(),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const platformApplicationProviders = ["douyin", "xhs", "taobao", "alibaba_1688", "jd", "pdd", "kuaishou", "wechat_shop"];
export const platformApplicationProviderSchema = z.enum(platformApplicationProviders);
export const connectionCreateSchema = z.object({
    tenantId: z.string().uuid(),
    provider: platformApplicationProviderSchema,
    kind: z.enum(["oauth", "api_key"]),
    name: z.string().trim().min(2).max(120),
    credentialId: z.string().uuid(),
    scopes: z.array(z.string().trim().min(1).max(120)).min(1).max(24),
    callbackConfirmed: z.literal(true),
    reason: z.string().trim().min(6).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const connectionProbeCreateSchema = z.object({
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const platformConnectionCatalog = [
    { provider: "douyin", label: "抖音电商", logoPath: "/platform/douyin-shop.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/douyin/callback", canProbe: true, canSubscribeEvents: true, officialUrl: "https://op.jinritemai.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "after_sale.read", label: "读取售后状态" }], authorizationGuide: "先在平台应用配置中保存抖音电商应用，再把店铺授权凭据写入保险库。", testSteps: ["确认官方应用回调地址一致", "选择同一企业的抖音店铺授权凭据", "保存后执行真实探测"] },
    { provider: "xhs", label: "小红书", logoPath: "/platform/xiaohongshu.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/xhs/callback", canProbe: false, canSubscribeEvents: true, officialUrl: "https://ark.xiaohongshu.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }], authorizationGuide: "小红书官方安全探测接口待确认前，只允许登记和投递事件，不显示自动验证通过。", testSteps: ["保存连接登记", "等待官方探测接口确认", "通过人工备注记录外部材料"] },
    { provider: "taobao", label: "淘宝/天猫", logoPath: "/platform/taobao.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/taobao/callback", canProbe: true, canSubscribeEvents: true, officialUrl: "https://open.taobao.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "refund.read", label: "读取退款状态" }], authorizationGuide: "先保存淘宝开放平台应用，再写入店铺 session 凭据。", testSteps: ["确认应用密钥有效", "选择同一企业的淘宝店铺授权凭据", "保存后执行真实探测"] },
    { provider: "alibaba_1688", label: "1688", logoPath: "/platform/1688.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/alibaba-1688/callback", canProbe: false, canSubscribeEvents: false, officialUrl: "https://open.1688.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "product.basic.read", label: "读取商品基础资料" }], authorizationGuide: "先在 1688 开放平台创建应用并完成审核，再登记当前系统生成的授权回调地址。", testSteps: ["应用与接口权限审核通过", "登记系统生成的真实授权回调地址", "真实探测适配器接入前不显示验证通过"] },
    { provider: "jd", label: "京东", logoPath: "/platform/jd.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/jd/callback", canProbe: false, canSubscribeEvents: false, officialUrl: "https://jos.jd.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "after_sale.read", label: "读取售后状态" }], authorizationGuide: "先在京东宙斯开放平台创建并发布应用，再登记当前系统生成的授权回调地址。", testSteps: ["应用与权限包审核通过", "登记系统生成的真实授权回调地址", "真实探测适配器接入前不显示验证通过"] },
    { provider: "pdd", label: "拼多多", logoPath: "/platform/pdd.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/pdd/callback", canProbe: true, canSubscribeEvents: true, officialUrl: "https://open.pinduoduo.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "refund.read", label: "读取退款状态" }], authorizationGuide: "先保存拼多多开放平台应用，再写入店铺 access token。", testSteps: ["确认应用 client 信息有效", "选择同一企业的拼多多店铺授权凭据", "保存后执行真实探测"] },
    { provider: "kuaishou", label: "快手电商", logoPath: "/platform/kuaishou.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/kuaishou/callback", canProbe: true, canSubscribeEvents: true, officialUrl: "https://open.kwaixiaodian.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "after_sale.read", label: "读取售后状态" }], authorizationGuide: "先保存快手电商应用，再写入店铺授权凭据。", testSteps: ["确认应用签名密钥有效", "选择同一企业的快手店铺授权凭据", "保存后执行真实探测"] },
    { provider: "wechat_shop", label: "微信小店", logoPath: "/platform/wechat-shop.png", kinds: ["oauth", "api_key"], callbackPath: "/api/platform-service-market/wechat-shop/callback", canProbe: true, canSubscribeEvents: true, officialUrl: "https://channels.weixin.qq.com", scopeOptions: [{ value: "shop.profile.read", label: "读取店铺身份" }, { value: "order.basic.read", label: "读取订单基础状态" }, { value: "after_sale.read", label: "读取售后状态" }], authorizationGuide: "写入微信小店授权凭据后，可通过官方基础信息接口做只读探测。", testSteps: ["确认店铺授权仍有效", "选择同一企业的微信小店授权凭据", "保存后执行真实探测"] },
];
export const webhookEventCatalog = [
    { value: "audit.event.created", label: "审计记录已创建" },
    { value: "connection.created", label: "平台连接已创建" },
    { value: "connection.revoked", label: "平台连接已停用" },
    { value: "feature_flag.changed", label: "功能开关已变更" },
    { value: "identity.configuration.changed", label: "企业登录配置已变更" },
    { value: "operations.incident.created", label: "平台事故已创建" },
    { value: "operations.incident.updated", label: "平台事故处置已更新" },
    { value: "operations.maintenance.created", label: "维护安排已创建" },
    { value: "operations.maintenance.cancelled", label: "维护安排已取消" },
    { value: "operations.maintenance.ended_early", label: "维护安排已提前结束" },
    { value: "operations.maintenance.completed", label: "维护安排已完成" },
    { value: "order.created", label: "订购单已创建" },
    { value: "payment.offline_confirmed", label: "线下收款已确认" },
    { value: "plan.version.published", label: "套餐版本已发布" },
    { value: "plan.version.stop_new_sales", label: "套餐版本已停止新购" },
    { value: "plan.version.archived", label: "套餐版本已归档" },
    { value: "platform_application_config.changed", label: "平台应用配置已变更" },
    { value: "print_agent_origin_policy.changed", label: "打印组件安全域名已变更" },
    { value: "quota_policy.changed", label: "额度策略已变更" },
    { value: "refund.executed", label: "退款已执行" },
    { value: "refund.execution_failed", label: "退款执行失败" },
    { value: "refund.requested", label: "退款申请已创建" },
    { value: "refund.approved", label: "退款申请已通过" },
    { value: "refund.rejected", label: "退款申请已驳回" },
    { value: "runtime.service_account.created", label: "服务账号已创建" },
    { value: "runtime.service_account.revoked", label: "服务账号已吊销" },
    { value: "runtime.service_account.rotated", label: "服务账号密钥已轮换" },
    { value: "runtime.service_account.actions.repaired", label: "服务账号权限已修复" },
    { value: "subscription.expired", label: "订阅已到期" },
    { value: "tenant.application.approved", label: "企业申请已通过" },
    { value: "tenant.application.rejected", label: "企业申请已驳回" },
    { value: "tenant.lifecycle.changed", label: "租户生命周期已变更" },
    { value: "tenant.organization_unit.changed", label: "企业归属已变更" },
    { value: "enterprise.organization_unit.member.changed", label: "组织单元成员已变更" },
    { value: "webhook.updated", label: "事件接收地址已更新" },
    { value: "*", label: "全部正式业务事件" },
];
export const webhookEventTypes = webhookEventCatalog.map((item) => item.value);
export const webhookEventTypeSchema = z.enum(webhookEventTypes);
export const webhookEventSelectionSchema = z.array(webhookEventTypeSchema).min(1).max(webhookEventCatalog.length)
    .refine((events) => new Set(events).size === events.length, "订阅事件不能重复")
    .refine((events) => !events.includes("*") || events.length === 1, "选择全部正式业务事件后不能再勾选单项事件");
const optionalWriteOnlySecret = z.string().trim().min(1).max(32_768).optional();
const platformApplicationChangeReason = z.string().trim().min(8).max(500);
const webhookSigningSecretSchema = z.string().trim().min(32).max(512);
export const platformApplicationConfigUpsertSchema = z.discriminatedUnion("provider", [
    z.object({
        provider: z.literal("douyin"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            serviceId: optionalWriteOnlySecret,
            appKey: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("xhs"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appId: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("taobao"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appKey: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("jd"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appKey: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("alibaba_1688"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appKey: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("pdd"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            clientId: optionalWriteOnlySecret,
            clientSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("kuaishou"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appKey: optionalWriteOnlySecret,
            appSecret: optionalWriteOnlySecret,
            signSecret: optionalWriteOnlySecret,
            messageSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
    z.object({
        provider: z.literal("wechat_shop"),
        reason: platformApplicationChangeReason,
        secrets: z.object({
            appId: optionalWriteOnlySecret,
            messageToken: optionalWriteOnlySecret,
            messageAesKey: optionalWriteOnlySecret,
            openComponentAppId: optionalWriteOnlySecret,
            openComponentAppSecret: optionalWriteOnlySecret,
            callbackGatewaySecret: optionalWriteOnlySecret,
        }).strict().default({}),
    }).strict(),
]);
export const webhookEndpointCreateSchema = z.object({
    tenantId: z.string().uuid().nullable(),
    url: z.string().url().max(2000),
    events: webhookEventSelectionSchema,
    signingSecret: webhookSigningSecretSchema,
    // 兼容旧前端传 null；真实签名密钥由调用方一次性提交，服务端只保存密文并返回元数据。
    secretCredentialId: z.literal(null).optional(),
}).strict();
export const webhookSecretRotateSchema = z.object({
    signingSecret: webhookSigningSecretSchema,
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
export const outboxOperatorActionSchema = z.object({
    reason: z.string().trim().min(8).max(500),
    idempotencyKey: z.string().trim().min(8).max(160),
}).strict();
const businessReasonSchema = z.string().trim().min(8).max(500);
const idempotencyKeySchema = z.string().trim().min(8).max(160);
export function isEnterpriseIdentityDomainName(value) {
    const domain = value.trim().toLowerCase();
    if (domain.length < 3 || domain.length > 253)
        return false;
    if (domain.includes("*"))
        return false;
    if (domain === "localhost" || domain.endsWith(".localhost") || domain.endsWith(".local"))
        return false;
    const labels = domain.split(".");
    if (labels.length < 2)
        return false;
    if (labels.some((label) => label.length < 1 || label.length > 63))
        return false;
    if (!labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label)))
        return false;
    const tld = labels[labels.length - 1];
    return /[a-z]/.test(tld) && !/^\d+$/.test(tld);
}
const domainNameSchema = z.string().trim().min(3).max(253)
    .transform((value) => value.toLowerCase())
    .refine(isEnterpriseIdentityDomainName, "请输入完整域名，例如 example.com");
export const enterpriseIdentityDomainInputSchema = z.object({
    domain: domainNameSchema,
}).strict();
export const enterpriseSsoModeSchema = z.enum(["disabled", "optional", "required"]);
export const enterpriseIdentityUpsertSchema = z.object({
    tenantId: z.string().uuid(),
    domains: z.array(enterpriseIdentityDomainInputSchema).max(20).default([]),
    sso: z.object({
        mode: enterpriseSsoModeSchema,
        providerLabel: z.string().trim().min(2).max(120).nullable().default(null),
        metadataUrl: z.string().trim().url().max(2000).nullable().default(null),
    }).strict(),
    loginPolicy: z.object({
        passwordLoginEnabled: z.boolean(),
        mfaRequired: z.boolean(),
        allowedDomainsOnly: z.boolean(),
    }).strict(),
    reason: businessReasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).strict().refine((value) => new Set(value.domains.map((item) => item.domain)).size === value.domains.length, {
    message: "企业域名不能重复",
    path: ["domains"],
});
export const identityConfigSchema = enterpriseIdentityUpsertSchema;
const organizationUnitNameSchema = z.string().trim().min(2).max(120);
const organizationUnitIdSchema = z.string().uuid();
const orgUnitMutationEvidenceSchema = z.object({
    reason: businessReasonSchema,
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const enterpriseOrgUnitPathItemSchema = z.object({
    id: organizationUnitIdSchema,
    name: organizationUnitNameSchema,
}).strict();
export const enterpriseOrgUnitTenantScopeSchema = z.object({
    tenantId: z.string().uuid(),
    tenantName: z.string().trim().min(1).max(120),
    tenantStatus: tenantStatusSchema,
}).strict();
const platformAdminStatusSchema = z.enum(["active", "suspended", "deleted"]);
export const enterpriseOrgUnitMemberSchema = z.object({
    adminId: z.string().uuid(),
    displayName: z.string().trim().min(1).max(80),
    email: z.string().trim().email().max(320),
    role: platformRoleSchema,
    status: platformAdminStatusSchema,
}).strict();
export const enterpriseOrgUnitMemberCandidateSchema = enterpriseOrgUnitMemberSchema.extend({
    currentUnitIds: z.array(organizationUnitIdSchema),
}).strict();
export const enterpriseOrgUnitTreeNodeSchema = z.lazy(() => z.object({
    id: organizationUnitIdSchema,
    name: organizationUnitNameSchema,
    parentId: organizationUnitIdSchema.nullable(),
    path: z.array(enterpriseOrgUnitPathItemSchema).min(1),
    depth: z.number().int().min(0).max(32),
    directMemberCount: z.number().int().min(0),
    directTenantCount: z.number().int().min(0),
    subtreeTenantCount: z.number().int().min(0),
    tenantScope: z.array(enterpriseOrgUnitTenantScopeSchema),
    members: z.array(enterpriseOrgUnitMemberSchema),
    canMove: z.boolean(),
    canDelete: z.boolean(),
    blockers: z.array(z.string().trim().min(1).max(200)),
    children: z.array(enterpriseOrgUnitTreeNodeSchema),
}).strict());
export const enterpriseOrgUnitTreeResponseSchema = z.object({
    items: z.array(enterpriseOrgUnitTreeNodeSchema),
    memberCandidates: z.array(enterpriseOrgUnitMemberCandidateSchema),
}).strict();
export const enterpriseOrgUnitCreateSchema = orgUnitMutationEvidenceSchema.extend({
    name: organizationUnitNameSchema,
    parentId: organizationUnitIdSchema.nullable().default(null),
}).strict();
export const enterpriseOrgUnitRenameSchema = orgUnitMutationEvidenceSchema.extend({
    name: organizationUnitNameSchema,
}).strict();
export const enterpriseOrgUnitMoveSchema = orgUnitMutationEvidenceSchema.extend({
    parentId: organizationUnitIdSchema.nullable(),
}).strict();
export const enterpriseOrgUnitDeleteSchema = orgUnitMutationEvidenceSchema;
export const enterpriseOrgUnitAssignTenantSchema = orgUnitMutationEvidenceSchema;
export const enterpriseOrgUnitAssignMemberSchema = orgUnitMutationEvidenceSchema;
export const enterpriseOrgUnitRemoveMemberSchema = orgUnitMutationEvidenceSchema;
export const auditStreamCreateSchema = z.object({
    tenantId: z.string().uuid().nullable(),
    destinationType: z.enum(["http", "s3", "splunk", "datadog"]),
    destination: z.string().trim().min(3).max(2000),
    secretCredentialId: z.string().uuid().nullable().default(null),
});
export const approvalTargetSelectionSchema = z.object({
    selectionToken: z.string().regex(/^apvsel_[A-Za-z0-9_-]+$/).max(5000).optional(),
    tenantId: z.string().uuid().optional(),
    adminId: z.string().uuid().optional(),
    adminEmail: z.string().trim().email().max(320).optional(),
    credentialId: z.string().uuid().optional(),
    planVersionId: z.string().uuid().optional(),
    addonVersionId: z.string().uuid().optional(),
    subscriptionChangeId: z.string().uuid().optional(),
    discountRuleId: z.string().uuid().optional(),
    purchaseOrderId: z.string().uuid().optional(),
    invoiceId: z.string().uuid().optional(),
    refundId: z.string().uuid().optional(),
    usageMeterId: z.string().uuid().optional(),
    supportGrantId: z.string().uuid().optional(),
    featureFlagKey: z.string().regex(/^[a-z0-9_.-]+$/).max(120).optional(),
    incidentId: z.string().uuid().optional(),
    maintenanceWindowId: z.string().uuid().optional(),
}).strict();
const approvalOperationTargetField = {
    "tenant.activate": "tenantId",
    "tenant.suspend": "tenantId",
    "tenant.close": "tenantId",
    "tenant.purge": "tenantId",
    "admin.create": "adminEmail",
    "admin.update": "adminId",
    "admin.delete": "adminId",
    "plan.publish": "planVersionId",
    "addon.publish": "addonVersionId",
    "plan.force_migration": "subscriptionChangeId",
    "subscription.change": "subscriptionChangeId",
    "discount_rule.approve": "discountRuleId",
    "payment.offline_confirm": "purchaseOrderId",
    "invoice.adjust": "invoiceId",
    "refund.approve": "refundId",
    "refund.execute": "refundId",
    "usage.adjust": "usageMeterId",
    "quota_override.create": "usageMeterId",
    "credential.rotate": "credentialId",
    "credential.revoke": "credentialId",
    "credential.rollback": "credentialId",
    "support.approve": "supportGrantId",
    "feature_flag.adjust": "featureFlagKey",
    "feature_flag.full_publish": "featureFlagKey",
    "incident.open": "incidentId",
    "incident.transition": "incidentId",
    "incident.resolve_with_recovery": "incidentId",
    "maintenance.schedule": "maintenanceWindowId",
    "maintenance.cancel": "maintenanceWindowId",
    "maintenance.end_early": "maintenanceWindowId",
    "feature_flag.schedule_change": "featureFlagKey",
    "feature_flag.rollback": "featureFlagKey",
};
export const approvalCreateSchema = z.object({
    operationType: approvalOperationTypeSchema,
    target: approvalTargetSelectionSchema,
    reason: z.string().trim().min(12).max(1000),
    payload: z.record(z.string(), z.unknown()).default({}),
    expiresAt: z.string().datetime(),
    idempotencyKey: idempotencyKeySchema,
}).strict().superRefine((value, ctx) => {
    const expected = approvalOperationTargetField[value.operationType];
    const present = Object.entries(value.target).filter(([, targetValue]) => targetValue !== undefined);
    if (value.target.selectionToken !== undefined) {
        if (present.length !== 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["target"],
                message: `审批操作 ${value.operationType} 使用目标选择令牌时不能同时提交其他目标字段`,
            });
        }
        return;
    }
    if (present.length !== 1 || present[0]?.[0] !== expected) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["target"],
            message: `审批操作 ${value.operationType} 必须且只能选择 ${expected}`,
        });
    }
});
const enterpriseApprovalTargetSelectionSchema = z.object({
    selectionToken: z.string().regex(/^apvsel_[A-Za-z0-9_-]+$/).max(5000),
}).strict();
export const enterpriseApprovalCreateSchema = z.object({
    operationType: approvalOperationTypeSchema,
    target: enterpriseApprovalTargetSelectionSchema,
    reason: z.string().trim().min(12).max(1000),
    payload: z.record(z.string(), z.unknown()).default({}),
    expiresAt: z.string().datetime(),
    idempotencyKey: idempotencyKeySchema,
}).strict();
const approvalBusinessInputOptionSchema = z.object({
    value: z.union([z.string(), z.number(), z.boolean()]),
    label: z.string().min(1).max(120),
    description: z.string().max(300).optional(),
}).strict();
export const approvalBusinessInputFieldSchema = z.object({
    key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_.-]{0,79}$/),
    label: z.string().min(1).max(120),
    type: z.enum(["text", "textarea", "select", "boolean", "percent", "money", "datetime", "secret_digest"]),
    required: z.boolean().default(true),
    readonly: z.boolean().default(false),
    options: z.array(approvalBusinessInputOptionSchema).default([]),
    helperText: z.string().max(300).optional(),
}).strict();
export const approvalOperationDirectoryItemSchema = z.object({
    operationType: approvalOperationTypeSchema,
    label: z.string().min(1).max(120),
    description: z.string().min(1).max(500),
    category: z.string().min(1).max(80),
    targetLabel: z.string().min(1).max(120),
    riskLevel: z.literal("high"),
    previewRequired: z.literal(true),
    inputFields: z.array(approvalBusinessInputFieldSchema).default([]),
    disabled: z.boolean().default(false),
    unavailableReason: z.string().min(1).max(300).nullable().optional(),
}).strict();
export const approvalOperationDirectoryResponseSchema = z.object({
    items: z.array(approvalOperationDirectoryItemSchema),
}).strict();
export const approvalTargetOptionSchema = z.object({
    operationType: approvalOperationTypeSchema,
    selectionToken: z.string().regex(/^apvsel_[A-Za-z0-9_-]+$/).max(5000),
    label: z.string().min(1).max(200),
    summary: z.string().min(1).max(500),
    tenantLabel: z.string().min(1).max(160).nullable().optional(),
    statusLabel: z.string().min(1).max(120).nullable().optional(),
    disabled: z.boolean().optional(),
    disabledReason: z.string().min(1).max(300).nullable().optional(),
}).strict();
export const approvalTargetOptionsResponseSchema = z.object({
    operationType: approvalOperationTypeSchema,
    items: z.array(approvalTargetOptionSchema),
}).strict();
export const enterpriseApprovalTargetOptionsResponseSchema = approvalTargetOptionsResponseSchema;
export const approvalPreviewSchema = z.object({
    operationType: approvalOperationTypeSchema,
    target: approvalTargetSelectionSchema,
    payload: z.record(z.string(), z.unknown()).default({}),
}).strict().superRefine((value, ctx) => {
    const expected = approvalOperationTargetField[value.operationType];
    const present = Object.entries(value.target).filter(([, targetValue]) => targetValue !== undefined);
    if (value.target.selectionToken !== undefined) {
        if (present.length !== 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["target"],
                message: `审批操作 ${value.operationType} 使用目标选择令牌时不能同时提交其他目标字段`,
            });
        }
        return;
    }
    if (present.length !== 1 || present[0]?.[0] !== expected) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["target"],
            message: `审批操作 ${value.operationType} 必须且只能选择 ${expected}`,
        });
    }
});
export const enterpriseApprovalPreviewSchema = z.object({
    operationType: approvalOperationTypeSchema,
    target: enterpriseApprovalTargetSelectionSchema,
    payload: z.record(z.string(), z.unknown()).default({}),
}).strict();
export const approvalPreviewResponseSchema = z.object({
    operationType: approvalOperationTypeSchema,
    target: z.object({
        selectionToken: z.string().regex(/^apvsel_[A-Za-z0-9_-]+$/).max(5000),
        label: z.string().min(1).max(200),
        summary: z.string().min(1).max(500),
    }).strict(),
    impact: z.array(z.string().min(1).max(500)).min(1),
    recovery: z.string().min(8).max(1000),
    canonicalBusinessInputs: z.record(z.string(), z.unknown()),
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const enterpriseApprovalPreviewResponseSchema = approvalPreviewResponseSchema;
export const approvalListItemSchema = z.object({
    id: z.string().min(1).max(120),
    operationType: approvalOperationTypeSchema,
    operationLabel: z.string().min(1).max(120),
    targetLabel: z.string().min(1).max(200),
    targetSummary: z.string().min(1).max(500),
    status: z.enum(["pending", "approved", "rejected", "expired", "executed"]),
    reason: z.string().min(1).max(1000),
    requestedByName: z.string().min(1).max(200),
    approvedByName: z.string().min(1).max(200).nullable().default(null),
    isSelfRequested: z.boolean().default(false),
    canDecide: z.boolean().default(false),
    impact: z.array(z.string().min(1).max(500)).min(1),
    recovery: z.string().min(8).max(1000),
    createdAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable().default(null),
    executedAt: z.string().datetime().nullable().default(null),
    expiresAt: z.string().datetime(),
}).strict();
export const approvalListResponseSchema = z.object({
    items: z.array(approvalListItemSchema),
}).strict();
export const enterpriseApprovalListResponseSchema = approvalListResponseSchema;
const opsSelectionTokenSchema = z.string().regex(/^opssel_[A-Za-z0-9_-]+$/).min(32).max(5000);
const opsImpactPreviewTokenSchema = z.string().regex(/^opsprev_[A-Za-z0-9_-]+$/).min(32).max(5000);
const opsScopeSchema = z.object({
    kind: z.enum(["tenant_selection", "tenant_group", "all_tenants"]).default("tenant_selection"),
    tenantSelectionTokens: z.array(opsSelectionTokenSchema).default([]),
    tenantGroupSelectionToken: opsSelectionTokenSchema.optional(),
    tenantGroupSelectionTokens: z.array(opsSelectionTokenSchema).default([]),
    allowListSelectionTokens: z.array(opsSelectionTokenSchema).default([]),
    rolloutPercent: z.number().int().min(0).max(100).optional(),
    services: z.array(z.string().trim().min(1).max(80)).default([]),
    blastRadius: z.enum(["single_tenant", "tenant_group", "all_tenants", "service"]).optional(),
    summary: z.string().trim().min(2).max(300).optional(),
}).strict();
const opsOwnerSelectionTokenSchema = opsSelectionTokenSchema;
const opsTimelineItemSchema = z.object({
    at: z.string().datetime(),
    status: z.enum(["detected", "investigating", "mitigating", "monitoring", "resolved"]),
    note: z.string().trim().min(4).max(1000),
}).strict();
const opsRecoveryPlanSchema = z.union([
    z.string().trim().min(12).max(2000),
    z.object({
        summary: z.string().trim().min(12).max(2000),
        fallbackOwnerSelectionToken: opsOwnerSelectionTokenSchema.optional(),
    }).strict(),
]);
const opsRecoveryEvidenceSchema = z.union([
    z.string().trim().min(8).max(2000),
    z.object({
        summary: z.string().trim().min(8).max(2000),
        verifiedAt: z.string().datetime().optional(),
        evidenceUrl: z.string().url().optional(),
    }).strict(),
]);
const opsPostmortemBaseSchema = z.object({
    required: z.boolean(),
    dueAt: z.string().datetime().optional(),
    ownerSelectionToken: opsOwnerSelectionTokenSchema.optional(),
}).strict();
const opsPostmortemSchema = opsPostmortemBaseSchema.superRefine((value, ctx) => {
    if (value.required && !value.dueAt) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dueAt"], message: "复盘必填截止时间" });
    }
});
const opsNotificationPlanSchema = z.object({
    channels: z.array(z.enum(["email", "sms", "in_app", "webhook"])).min(1),
    firstNoticeAt: z.string().datetime().optional(),
    reminderAt: z.string().datetime().optional(),
    message: z.string().trim().min(12).max(2000),
}).strict();
const opsWindowSchema = z.object({
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    timezone: z.string().trim().min(2).max(80),
}).strict().refine((value) => new Date(value.endsAt) > new Date(value.startsAt), { message: "结束时间必须晚于开始时间", path: ["endsAt"] });
export const incidentCreateSchema = z.object({
    title: z.string().trim().min(4).max(200),
    severity: z.enum(["sev1", "sev2", "sev3", "sev4"]),
    description: z.string().trim().min(12).max(4000),
    scope: opsScopeSchema,
    ownerSelectionToken: opsOwnerSelectionTokenSchema,
    timeline: z.array(opsTimelineItemSchema).min(1),
    recoveryPlan: opsRecoveryPlanSchema,
    postmortem: opsPostmortemSchema,
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const incidentTransitionSchema = z.object({
    status: z.enum(["open", "monitoring", "resolved"]),
    note: z.string().trim().min(8).max(1000),
    ownerSelectionToken: opsOwnerSelectionTokenSchema.optional(),
    timeline: z.array(opsTimelineItemSchema).min(1).max(20).optional(),
    recoveryEvidence: opsRecoveryEvidenceSchema.optional(),
    postmortem: opsPostmortemBaseSchema.extend({
        status: z.enum(["pending", "completed"]).default("pending"),
        summary: z.string().trim().min(8).max(2000).optional(),
    }).superRefine((value, ctx) => {
        if (value.required && !value.dueAt) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dueAt"], message: "复盘必填截止时间" });
        }
    }).optional(),
    idempotencyKey: idempotencyKeySchema,
}).strict().superRefine((value, ctx) => {
    if (value.status === "resolved" && !value.recoveryEvidence) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recoveryEvidence"], message: "事故恢复必须提供恢复证据" });
    }
    if (value.status === "resolved" && !value.postmortem) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["postmortem"], message: "事故恢复必须进入复盘" });
    }
});
export const maintenanceCreateSchema = z.object({
    title: z.string().trim().min(4).max(200),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    message: z.string().trim().min(12).max(2000),
    window: opsWindowSchema,
    scope: opsScopeSchema,
    notificationPlan: opsNotificationPlanSchema,
    cancellation: z.object({
        allowedUntil: z.string().datetime(),
        reasonRequired: z.boolean().default(true),
    }).strict(),
    earlyEnd: z.object({
        allowedAfterStart: z.boolean().default(true),
        recoveryRequired: z.boolean().default(true),
    }).strict(),
    idempotencyKey: idempotencyKeySchema,
}).strict().refine((value) => value.startsAt === value.window.startsAt && value.endsAt === value.window.endsAt, { message: "维护窗口时间必须与页面时间一致", path: ["window"] });
export const featureFlagUpsertSchema = z.object({
    catalogKey: z.string().regex(/^[a-z0-9][a-z0-9.-]{2,119}$/),
    environment: z.enum(["development", "staging", "production"]),
    scope: opsScopeSchema,
    schedule: z.object({
        plannedAt: z.string().datetime(),
        autoExpireAt: z.string().datetime().optional(),
    }).strict(),
    impactPreviewToken: opsImpactPreviewTokenSchema,
    approvalId: z.string().uuid(),
    rollbackPlan: z.object({
        mode: z.enum(["restore_previous", "disable", "set_percent"]),
        previousRolloutPercent: z.number().int().min(0).max(100).optional(),
        expireAt: z.string().datetime().optional(),
    }).strict(),
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const featureFlagImpactPreviewSchema = featureFlagUpsertSchema
    .omit({ approvalId: true, impactPreviewToken: true, idempotencyKey: true })
    .strict();
export const maintenanceMutationSchema = z.object({
    reason: z.string().trim().min(8).max(1000),
    recoveryEvidence: z.string().trim().min(8).max(2000).optional(),
    notificationPlan: opsNotificationPlanSchema.optional(),
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const featureFlagRollbackSchema = z.object({
    catalogKey: z.string().regex(/^[a-z0-9][a-z0-9.-]{2,119}$/),
    environment: z.enum(["development", "staging", "production"]),
    rollbackPlan: z.object({
        mode: z.enum(["restore_previous", "disable", "set_percent"]),
        previousRolloutPercent: z.number().int().min(0).max(100).optional(),
        expireAt: z.string().datetime().optional(),
    }).strict().optional(),
    approvalId: z.string().uuid(),
    reason: z.string().trim().min(8).max(1000),
    idempotencyKey: idempotencyKeySchema,
}).strict();
export const assistantChatMessageSchema = z.object({
    role: z.enum(["user", "assistant"]),
    content: z.string().trim().min(1).max(4000),
    images: z.array(z.object({
        name: z.string().trim().max(120),
        mimeType: z.string().regex(/^image\/(png|jpeg|jpg|webp|gif)$/),
        dataUrl: z.string().startsWith("data:image/").max(2_200_000),
    })).max(3).optional(),
});
export const assistantChatRequestSchema = z.object({
    messages: z.array(assistantChatMessageSchema).min(1).max(20),
    pagePath: z.string().trim().max(200).optional(),
});
const businessVisiblePathSchema = z.string().trim().min(1).max(200).regex(/^\/[A-Za-z0-9/_?&=.#-]*$/);
const businessCopyForbiddenPattern = /(POST|GET|PATCH|DELETE)\s+\/api|\/api\/|UUID|idempotency|payload|procedure|tenantId|targetId|operationType|actionType|selectionToken|status\s*=|token/i;
const businessCopyTextSchema = (max = 1000) => z.string().trim().min(1).max(max).refine((value) => !businessCopyForbiddenPattern.test(value), {
    message: "面向操作者的帮助文案不能直出接口、内部字段或技术编号",
});
export const assistantAnswerSourceSchema = z.object({
    pagePath: businessVisiblePathSchema,
    pageTitle: z.string().trim().min(1).max(120),
    dataUpdatedAt: z.string().datetime(),
    evidenceLabel: z.string().trim().min(2).max(200),
}).strict();
export const assistantSpeculationSchema = z.object({
    hasSpeculation: z.boolean(),
    notes: z.array(z.string().trim().min(2).max(300)).max(5),
}).strict().superRefine((value, ctx) => {
    if (value.hasSpeculation && value.notes.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["notes"], message: "包含推测时必须明确说明推测依据与待确认事实" });
    }
});
export const assistantNextActionSchema = z.object({
    kind: z.enum(["navigate", "ask", "open_manual", "retry", "contact"]),
    label: z.string().trim().min(1).max(120),
    pagePath: businessVisiblePathSchema.optional(),
    prompt: z.string().trim().min(1).max(1000).optional(),
}).strict().superRefine((value, ctx) => {
    if (value.kind === "navigate" && !value.pagePath) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["pagePath"], message: "跳转动作必须提供页面入口" });
    }
    if ((value.kind === "ask" || value.kind === "open_manual") && !value.prompt) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["prompt"], message: "追问或手册动作必须提供业务问题" });
    }
});
export const assistantChatResponseSchema = z.object({
    reply: z.string().trim().min(1).max(8000),
    provider: aiProviderSchema,
    model: z.string().trim().min(1).max(160),
    readOnly: z.literal(true),
    sources: z.array(assistantAnswerSourceSchema).min(1).max(8),
    speculation: assistantSpeculationSchema,
    nextActions: z.array(assistantNextActionSchema).max(5).default([]),
    fallbackApplied: z.object({
        fromProvider: aiProviderSchema,
        fromModel: z.string().trim().min(1).max(160),
        reason: z.string().trim().min(1).max(200),
    }).strict().nullable().optional(),
}).strict();
const manualTaskSourcePageSchema = z.object({
    pagePath: businessVisiblePathSchema,
    pageTitle: z.string().trim().min(1).max(120),
}).strict();
export const manualTaskGuideSchema = z.object({
    taskKey: z.string().trim().min(3).max(120).regex(/^[a-z0-9][a-z0-9.-]*$/),
    title: businessCopyTextSchema(120),
    sourcePage: manualTaskSourcePageSchema,
    dataUpdatedAt: z.string().datetime(),
    whatYouCanDo: z.array(businessCopyTextSchema()).min(1).max(8),
    whenToUse: z.array(businessCopyTextSchema()).min(1).max(8),
    howToRecover: z.array(businessCopyTextSchema()).min(1).max(8),
    emptyState: z.object({
        message: businessCopyTextSchema(300),
        nextAction: assistantNextActionSchema,
    }).strict(),
    forbiddenCopy: z.array(z.string()).max(0).default([]),
}).strict();
export const manualTaskGuideResponseSchema = z.object({
    generatedAt: z.string().datetime(),
    tasks: z.array(manualTaskGuideSchema).min(1),
}).strict();
export const assistantContextPageHelpSchema = z.object({
    path: businessVisiblePathSchema,
    title: z.string().trim().min(1).max(120),
    plainName: z.string().trim().min(1).max(120),
    summary: businessCopyTextSchema(500),
    whatYouCanDo: z.array(businessCopyTextSchema()).min(1).max(12),
    whenToUse: z.array(businessCopyTextSchema()).min(1).max(12),
    howToRecover: z.array(businessCopyTextSchema()).min(1).max(12),
    firstLook: z.array(businessCopyTextSchema()).min(1).max(8),
    fields: z.array(z.object({
        name: z.string().trim().min(1).max(120),
        meaning: businessCopyTextSchema(500),
        source: businessCopyTextSchema(500),
        example: businessCopyTextSchema(300),
        rules: businessCopyTextSchema(500),
        risk: businessCopyTextSchema(500),
    }).strict()).max(30),
    operations: z.array(z.object({
        title: z.string().trim().min(1).max(120),
        steps: z.array(businessCopyTextSchema()).min(1).max(12),
        success: businessCopyTextSchema(500),
        failure: businessCopyTextSchema(500),
        danger: z.enum(["low", "medium", "high"]),
    }).strict()).max(20),
    taskGuides: z.array(manualTaskGuideSchema).min(1).max(12),
    successSignals: z.array(businessCopyTextSchema()).min(1).max(12),
    commonProblems: z.array(businessCopyTextSchema()).min(1).max(12),
    riskNotes: z.array(businessCopyTextSchema()).max(12),
    suggestedPrompts: z.array(z.string().trim().min(1).max(200)).min(1).max(12),
}).strict();
// ---- 商业底座与能力目录契约（P0）----
export * from "./capabilities.js";
export * from "./commercial.js";
export * from "./audit-events.js";
export * from "./category-attributes.js";
